<template>
  <FriedModalSubContainer>
    <template #header>
      <FriedModalHeader :header="t('cr_sign_using_electronic_id')" />
    </template>
    <FriedParagraph>
      {{ t('cr_select_eid_provider') }}
    </FriedParagraph>
    <FriedMessage
      v-if="signError"
      class="error"
      data-external="eid-sign-error-message"
      :title="signErrorMessage.title"
      type="error"
    >
      {{ signErrorMessage.description }}
    </FriedMessage>
    <div class="eid-item-list">
      <EidItem v-model="selectedEid" :eid-type="EidType.SE" :label="t('cr_bankid_sverige')">
        <template #logo>
          <BankIdSwedenLogo class="logo" />
        </template>
        <BankIdSweden @sign="$emit('sign')" />
      </EidItem>
      <EidItem v-model="selectedEid" :eid-type="EidType.NO" :label="t('cr_bankid_norge')">
        <template #logo><BankIdNorwayLogo class="logo" /></template>
        <CriiptoMethod :eid-type="EidType.NO" @sign="$emit('sign')" />
      </EidItem>
      <EidItem
        v-model="selectedEid"
        :eid-type="EidType.BankIDFI"
        :label="t('cr_finnish_trust_network')"
      >
        <template #logo><BankIdFinlandLogo class="logo" /></template>
        <CriiptoMethod :eid-type="EidType.BankIDFI" @sign="$emit('sign')" />
      </EidItem>
      <EidItem v-model="selectedEid" :eid-type="EidType.MitID" :label="t('cr_mitid')">
        <template #logo><MitIdLogo class="logo" /></template>
        <CriiptoMethod :eid-type="EidType.MitID" @sign="$emit('sign')" />
      </EidItem>
    </div>
    <template #footer>
      <FriedModalButtonsFooter>
        <FriedButton
          button-type="secondary"
          data-external="eid-signing-cancel-button"
          @click="$emit('close')"
        >
          {{ $t('cancel') }}
        </FriedButton>
      </FriedModalButtonsFooter>
    </template>
  </FriedModalSubContainer>
</template>
<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { computed, onBeforeUnmount, ref } from 'vue';
import type { SigningPageRecipient } from '@getaccept/lib-shared/src/types/signing-page-recipient';
import { EidType } from '../../types/enums/eid-type';
import BankIdSwedenLogo from '../../assets/bankid.svg';
import BankIdNorwayLogo from '../../assets/bankid-no.svg';
import BankIdFinlandLogo from '../../assets/nemid.svg';
import MitIdLogo from '../../assets/mitid-dark.svg';
import { EidHelper } from '../../helpers/eid.helper';
import { useEntityStore } from '../../../entity/store/entity.store';
import { userLanguage } from '../../../lang';
import { useResumeEidSigning } from '../composables/resume-eid-signing.composable';
import type { SignError } from '../../types/sign';
import { SigningHelper } from '../../helpers/signing.helper';
import BankIdSweden from './BankIdSweden.vue';
import EidItem from './EidItem.vue';
import CriiptoMethod from './CriiptoMethod.vue';

interface Props {
  recipient: SigningPageRecipient;
  reviewPending: boolean;
  signError: SignError | null;
}

const props = defineProps<Props>();
defineEmits(['sign', 'close']);

const { t } = useI18n();
const { eidType: resumedEidType, resetQueryParams } = useResumeEidSigning();

const entityStore = useEntityStore();

const defaultEidType = computed(() => {
  const userLang = userLanguage();
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
  const entityCountryCode = entityStore.entity?.countryCode;

  return EidHelper.getDefaultMethod(timeZone, userLang, entityCountryCode);
});

const signErrorMessage = computed(() => SigningHelper.getSignErrorMessage(props.signError));

const selectedEid = ref(resumedEidType.value || defaultEidType.value);

onBeforeUnmount(() => resetQueryParams());
</script>
<style lang="scss" scoped>
.error {
  margin: var(--spacing-100) 0;
}

.eid-item-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-100);
  padding: var(--spacing-25);
  margin-top: var(--spacing-75);

  .logo {
    height: 100%;
  }

  .sign-button {
    width: 100%;
  }
}
</style>
